'use client';

import { cn } from '@magicschool/spellbook';
import type { ComponentProps } from 'react';
import { useIntl } from 'react-intl';
import { analyticsTrack } from '@/util/amplitude/analyticsClient';

export interface AccessiBeFabProps extends ComponentProps<'button'> {
  /** Additional CSS classes */
  className?: string;
}

/**
 * Custom FAB (Floating Action Button) for the accessiBe widget.
 * Tab popout positioned on the right edge with the accessiBe people icon.
 */
export function AccessiBeFab({ className, onClick, ...props }: AccessiBeFabProps) {
  const intl = useIntl();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    analyticsTrack('Navigation:AccessiBeHelper');
    onClick?.(event);
  };

  return (
    <button
      type="button"
      aria-label={intl.formatMessage({ id: 'accessibe.fab.label' })}
      data-acsb-custom-trigger="true"
      onClick={handleClick}
      className={cn(
        'group fixed z-[2147483647] pointer-events-auto',
        'bottom-3 right-0 lg:bottom-8',
        'rounded-l-lg',
        'p-2 lg:px-2.5 lg:py-2',
        'text-white',
        'cursor-pointer',
        'transition-all duration-200 ease-in-out',
        'hover:scale-115',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#146ff8]',
        className,
      )}
      style={{
        backgroundColor: '#146ff8',
      }}
      {...props}
    >
      <span className="flex h-full w-full items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 131.3" className="h-4 w-4 lg:h-5 lg:w-5" aria-hidden="true" fill="white">
          <path
            fill="white"
            d="M71.6 131.3c1 0 2.1-.3 3.1-.8 3.9-1.8 5.5-6.2 3.6-10.1 0 0-14.3-32.7-16.9-44.7-1-4.2-1.6-15.3-1.8-20.5 0-1.8 1-3.4 2.6-3.9l32-9.6c3.9-1 6.2-5.5 5.2-9.4-1-3.9-5.5-6.2-9.4-5.2 0 0-29.6 9.6-40.3 9.6-10.4 0-39.8-9.4-39.8-9.4-3.9-1-8.3.8-9.6 4.7-1.3 4.2 1 8.6 5.2 9.6l32 9.6c1.6.5 2.9 2.1 2.6 3.9-.3 5.2-.8 16.4-1.8 20.5-2.6 12-16.9 44.7-16.9 44.7-1.8 3.9 0 8.3 3.6 10.1 1 .5 2.1.8 3.1.8 2.9 0 5.7-1.6 6.8-4.4l15.3-31.2L64.8 127c1.3 2.7 3.9 4.3 6.8 4.3z"
          />
          <circle cx="50.3" cy="14.6" r="14.6" fill="white" />
        </svg>
      </span>
    </button>
  );
}
